/**
 * MC STOCH — THE V-SPLIT. A faithful port of the estate's canonical implementation.
 *
 * ⛔⛔ WHAT THE V-SPLIT ACTUALLY IS, AND WHY A FIRST ATTEMPT GOT IT WRONG.
 * Owner word 2026-08-23 ~17:0xZ: *"it's the v split … from Market Sypher … we have got this on the
 * BTC hub … it's not what you're looking at so far."* The Pine source settles it in its
 * **Visuals** block — not its inputs:
 * ```
 *   plot(mc_fast, "MC Fast (Pink)", color=color.fuchsia, linewidth=1)
 *   plot(mc_slow, "MC Slow", color=(mc_slow < mc_fast ? green : red), linewidth=2)
 * ```
 * ⇒ ***THE V-SPLIT IS MC STOCH 40/81 ON HEIKIN ASHI — NOT RSI.*** That script also computes
 * `rsi_fast`/`rsi_slow` (7/14) and ships them in its webhook payload, but NEVER PLOTS THEM.
 * ⇒ ⭐⭐ ***A READER WHO SKIMS THE INPUTS AND SKIPS THE PLOT CALLS BUILDS THE WRONG PICTURE.***
 * DESK-n26 R544(2) settled the naming that came out of it: **"V-SPLIT" IS THIS**; the pane over
 * `/api/es/vsplit` (RSI-7 at two RUNGS, 12m/480m) renames to "RSI SPLIT". Two panes, two names.
 *
 * ⛔ THIS IS A PORT, NOT AN IMPLEMENTATION. The authority is
 * `alphaops-overlay-hub/src/indicators.py::compute_mc_stoch` — 1,198 references to `mc_fast` in
 * the estate, and `reproduction_oracle.py::compare_mc_stoch` already treats it as canonical. The
 * BTC hub renders those numbers today.
 * ⇒ ***EVERY QUIRK IS COPIED DELIBERATELY, INCLUDING THE ONES I WOULD HAVE WRITTEN DIFFERENTLY***
 * — Pine's `rng === 0 -> 50`, and the sliding-SMA's `validCount` bookkeeping. A "cleaner" version
 * would disagree with the number on the owner's TradingView, and being right in isolation is
 * worth nothing here (R544(1)).
 * `mcStoch.test.ts` grades this against output generated BY that Python over REAL ES candles, so
 * no hand wrote the expected values.
 */

export interface Candle {
  open: number;
  high: number;
  low: number;
  close: number;
}

/** Pine's lengths, from `indicators.py`. Do not tune these locally — a retune is a fork. */
export const MC_FAST_STOCH_LEN = 40;
export const MC_SLOW_STOCH_LEN = 81;
export const MC_K_SMOOTH = 2;
/** Bars required before the smoothed legs can emit: stoch length plus the second SMA sample. */
export const MC_STOCH_WARMUP_BARS = Object.freeze({
  fast: MC_FAST_STOCH_LEN + MC_K_SMOOTH - 1,
  slow: MC_SLOW_STOCH_LEN + MC_K_SMOOTH - 1,
});

export type McState = "CROSS" | "RISING" | "FALLING" | "UNKNOWN";

export interface McBar {
  mcFast: number | null;
  mcSlow: number | null;
  mcSpread: number | null;
  mcCrossUp: boolean;
  mcCrossDown: boolean;
  mcState: McState;
}

export interface McWarmupState {
  readonly fast: { readonly have: number; readonly need: number };
  readonly slow: { readonly have: number; readonly need: number };
}

export function mcStochLegend(warmup: McWarmupState): string {
  if (warmup.fast.have < warmup.fast.need) {
    return `INSUFFICIENT DATA ${warmup.fast.have}/${warmup.fast.need} (fast) · ${warmup.slow.have}/${warmup.slow.need} (slow)`;
  }
  if (warmup.slow.have < warmup.slow.need) {
    return `INSUFFICIENT DATA ${warmup.slow.have}/${warmup.slow.need} (slow)`;
  }
  return "MC Stoch 40/81";
}

/** Array-compatible so every established numeric consumer stays byte-for-byte unchanged. */
export type McStochOutput = McBar[] & { readonly warmup: McWarmupState };

/**
 * HA_close = (o+h+l+c)/4 · HA_open[0] = open[0] · HA_open[i] = (HA_open[i-1] + HA_close[i-1])/2
 * HA_high = max(high, HA_open, HA_close) · HA_low = min(low, HA_open, HA_close)
 */
export function heikinAshi(candles: Candle[]): { haHigh: number[]; haLow: number[]; haClose: number[] } {
  const n = candles.length;
  const haOpen = new Array<number>(n).fill(0);
  const haHigh = new Array<number>(n).fill(0);
  const haLow = new Array<number>(n).fill(0);
  const haClose = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++) {
    const c = candles[i];
    haClose[i] = (c.open + c.high + c.low + c.close) / 4;
    haOpen[i] = i === 0 ? c.open : (haOpen[i - 1] + haClose[i - 1]) / 2;
    haHigh[i] = Math.max(c.high, haOpen[i], haClose[i]);
    haLow[i] = Math.min(c.low, haOpen[i], haClose[i]);
  }
  return { haHigh, haLow, haClose };
}

/**
 * Pine `ta.stoch`. `null` through the first `length - 1` bars.
 * ⛔ `rng === 0 -> 50`, NOT null and NOT 0 — Pine returns the midpoint on a flat window. A
 * zero-range window is ordinary on a quiet 1m rung, so returning 0 would drag the line to the
 * floor exactly when the market is doing nothing: a confident picture of a collapse that never
 * happened. This branch is load-bearing, not academic.
 */
export function stoch(close: number[], high: number[], low: number[], length: number): (number | null)[] {
  const n = close.length;
  const out = new Array<number | null>(n).fill(null);
  if (length <= 0 || n < length) return out;
  for (let i = length - 1; i < n; i++) {
    let hi = -Infinity, lo = Infinity;
    for (let j = i - length + 1; j <= i; j++) {
      if (high[j] > hi) hi = high[j];
      if (low[j] < lo) lo = low[j];
    }
    const rng = hi - lo;
    out[i] = rng === 0 ? 50 : ((close[i] - lo) / rng) * 100;
  }
  return out;
}

/**
 * Pine `ta.sma` over a possibly-sparse series.
 * ⚠ THE `validCount` BOOKKEEPING IS COPIED VERBATIM from the canonical Python, including the
 * branch that INCREMENTS when a `null` drops out of the window. I did not "fix" it: the oracle is
 * that code's output and the estate renders from it.
 */
export function sma(values: (number | null)[], period: number): (number | null)[] {
  const n = values.length;
  const out = new Array<number | null>(n).fill(null);
  if (period <= 0 || n < period) return out;

  let windowSum = 0, validCount = 0;
  for (let i = 0; i < period; i++) {
    const v = values[i];
    if (v !== null) { windowSum += v; validCount++; }
  }
  if (validCount === period) out[period - 1] = windowSum / period;

  for (let i = period; i < n; i++) {
    const oldVal = values[i - period];
    const newVal = values[i];
    if (oldVal !== null) windowSum -= oldVal; else validCount++;
    if (newVal !== null) windowSum += newVal; else validCount--;
    out[i] = validCount === period ? windowSum / period : null;
  }
  return out;
}

/** Pine `ta.crossover`: a[i] > b[i] AND a[i-1] <= b[i-1]. A null on either side is no cross. */
export function crossOver(a: (number | null)[], b: (number | null)[]): boolean[] {
  const out = new Array<boolean>(a.length).fill(false);
  for (let i = 1; i < a.length; i++) {
    const ai = a[i], bi = b[i], ap = a[i - 1], bp = b[i - 1];
    if (ai === null || bi === null || ap === null || bp === null) continue;
    out[i] = ai > bi && ap <= bp;
  }
  return out;
}

/** Pine `ta.crossunder`: a[i] < b[i] AND a[i-1] >= b[i-1]. */
export function crossUnder(a: (number | null)[], b: (number | null)[]): boolean[] {
  const out = new Array<boolean>(a.length).fill(false);
  for (let i = 1; i < a.length; i++) {
    const ai = a[i], bi = b[i], ap = a[i - 1], bp = b[i - 1];
    if (ai === null || bi === null || ap === null || bp === null) continue;
    out[i] = ai < bi && ap >= bp;
  }
  return out;
}

/** The canonical rounding the Python applies before anything downstream sees a number. */
const round4 = (v: number | null): number | null =>
  v === null || !Number.isFinite(v) ? null : Math.round(v * 1e4) / 1e4;

/**
 * MC Stoch over regular candles — HA, then two stochastics, then a 2-bar SMA on each.
 *
 * `mcState` mirrors the Python exactly: a CROSS bar is CROSS whichever way it crossed; otherwise
 * RISING when fast > slow, FALLING when not.
 * ⚠ "RISING" is a statement about the RELATION between the two series, NOT about slope. The name
 * is the estate's and a port does not rename things.
 */
export function computeMcStoch(candles: Candle[]): McStochOutput {
  const n = candles.length;
  const { haHigh, haLow, haClose } = heikinAshi(candles);
  const mcFast = sma(stoch(haClose, haHigh, haLow, MC_FAST_STOCH_LEN), MC_K_SMOOTH);
  const mcSlow = sma(stoch(haClose, haHigh, haLow, MC_SLOW_STOCH_LEN), MC_K_SMOOTH);
  const up = crossOver(mcFast, mcSlow);
  const dn = crossUnder(mcFast, mcSlow);

  const out = [] as unknown as McStochOutput;
  for (let i = 0; i < n; i++) {
    const f = mcFast[i], s = mcSlow[i];
    const spread = f !== null && s !== null ? f - s : null;
    let state: McState;
    if (up[i] || dn[i]) state = "CROSS";
    else if (f !== null && s !== null) state = f > s ? "RISING" : "FALLING";
    else state = "UNKNOWN";
    out.push({
      mcFast: round4(f), mcSlow: round4(s), mcSpread: round4(spread),
      mcCrossUp: up[i], mcCrossDown: dn[i], mcState: state,
    });
  }
  Object.defineProperty(out, "warmup", {
    value: Object.freeze({
      fast: Object.freeze({ have: n, need: MC_STOCH_WARMUP_BARS.fast }),
      slow: Object.freeze({ have: n, need: MC_STOCH_WARMUP_BARS.slow }),
    }),
    enumerable: false,
  });
  return out;
}
